// Alliance members (spec §3.6): roster with ranks; rank changes and kicks from Warlord, leadership
// transfer for the Overlord. The controls appear only after Edit (Tim, 16c feedback).
import { type AllianceRank, type MemberAlliance, RANK_ORDER } from '@stars-and-ships/api-types';
import { useMutation } from '@tanstack/react-query';
import { Crown, Users } from 'lucide-react';
import { useState } from 'react';
import { useOutletContext } from 'react-router';
import { endpoints } from '../../api/endpoints';
import type { GameContext } from '../GameLayout';
import { PlayerLink } from '../ProfileLinks';
import { Button, ErrorNotice, Panel, Select } from '../ui';
import { assignableRanks, atLeast, ConfirmButton, RANK_LABELS, useRefresh } from './shared';

export function MembersTab({ galaxyId, alliance }: { galaxyId: string; alliance: MemberAlliance }) {
  const { me } = useOutletContext<GameContext>();
  const refresh = useRefresh(galaxyId);
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState(false);
  const canManage = atLeast(alliance.myRank, 'warlord');
  const handlers = {
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: setError,
  };
  const rank = useMutation({
    mutationFn: (v: { playerId: string; rank: Exclude<AllianceRank, 'overlord'> }) =>
      endpoints.changeRank(galaxyId, alliance.id, v.playerId, v.rank),
    ...handlers,
  });
  const kick = useMutation({
    mutationFn: (playerId: string) => endpoints.removeMember(galaxyId, alliance.id, playerId),
    ...handlers,
  });
  const lead = useMutation({
    mutationFn: (playerId: string) => endpoints.transferLeadership(galaxyId, alliance.id, playerId),
    ...handlers,
  });
  const pending = rank.isPending || kick.isPending || lead.isPending;
  return (
    <Panel
      title="Members"
      actions={
        canManage ? (
          <Button variant="ghost" aria-pressed={editing} onClick={() => setEditing((on) => !on)}>
            {editing ? 'Done' : 'Edit'}
          </Button>
        ) : null
      }
    >
      {error ? <ErrorNotice error={error} /> : null}
      <ul className="space-y-2">
        {alliance.members.map((member) => {
          const self = member.playerId === me?.playerId;
          const ranks = self || !editing ? [] : assignableRanks(alliance.myRank, member.rank);
          const canKick =
            editing &&
            !self &&
            member.rank !== 'overlord' &&
            atLeast(alliance.myRank, 'warlord') &&
            RANK_ORDER[member.rank] <= RANK_ORDER[alliance.myRank];
          const canLead = editing && alliance.myRank === 'overlord' && !self;
          return (
            <li
              key={member.playerId}
              data-member={member.displayName}
              data-member-rank={member.rank}
              className="hud-cut-sm border border-line bg-void/60 p-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-2 text-sm text-text">
                  {member.rank === 'overlord' ? (
                    <Crown className="size-4 text-accent" aria-hidden />
                  ) : (
                    <Users className="size-4 text-faint" aria-hidden />
                  )}
                  <PlayerLink
                    galaxyId={galaxyId}
                    playerId={member.playerId}
                    name={member.displayName}
                  />
                  {self ? <span className="text-xs text-faint">(you)</span> : null}
                  <span className="text-xs text-muted">
                    {RANK_LABELS[member.rank]} · {member.points} pts
                  </span>
                </p>
                {ranks.length > 0 ? (
                  <Select
                    label={`Rank of ${member.displayName}`}
                    hideLabel
                    value={member.rank}
                    disabled={pending}
                    onChange={(e) =>
                      rank.mutate({
                        playerId: member.playerId,
                        rank: e.target.value as Exclude<AllianceRank, 'overlord'>,
                      })
                    }
                  >
                    {ranks.includes(member.rank) ? null : (
                      <option value={member.rank}>{RANK_LABELS[member.rank]}</option>
                    )}
                    {ranks.map((r) => (
                      <option key={r} value={r}>
                        {RANK_LABELS[r]}
                      </option>
                    ))}
                  </Select>
                ) : null}
              </div>
              {canKick || canLead ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {canKick ? (
                    <ConfirmButton
                      label="Remove"
                      question={`Remove ${member.displayName}? They cannot rejoin this alliance for 48 hours, and their reinforcements in your systems fly home.`}
                      confirmLabel="Remove"
                      variant="ghost"
                      disabled={pending}
                      onConfirm={() => kick.mutate(member.playerId)}
                    />
                  ) : null}
                  {canLead ? (
                    <ConfirmButton
                      label="Make leader"
                      question={`Make ${member.displayName} the Overlord? You become a Warlord.`}
                      confirmLabel="Hand over"
                      variant="ghost"
                      disabled={pending}
                      onConfirm={() => lead.mutate(member.playerId)}
                    />
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}
